import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCreationStore } from '../lib/creationStore'
import { StepIdentity } from '../components/creation/StepIdentity'
import { StepBirthday } from '../components/creation/StepBirthday'
import { StepAppearance } from '../components/creation/StepAppearance'
import { StepAttributes } from '../components/creation/StepAttributes'
import { StepOccupation } from '../components/creation/StepOccupation'
import { StepReview } from '../components/creation/StepReview'

const STEPS = [
  { component: StepIdentity, label: 'Identity' },
  { component: StepBirthday, label: 'Birthday' },
  { component: StepAppearance, label: 'Appearance' },
  { component: StepAttributes, label: 'Attributes' },
  { component: StepOccupation, label: 'Occupation' },
  { component: StepReview, label: 'Review' },
]

export function CharacterCreationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const store = useCreationStore()
  const setField = useCreationStore(s => s.setField)

  useEffect(() => {
    const cid = searchParams.get('campaign')
    if (cid) setField('campaignId', cid)
  }, [searchParams, setField])

  const CurrentStep = STEPS[store.step - 1]?.component
  const isLast = store.step === STEPS.length
  const isFirst = store.step === 1

  const handleCreate = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const { data, error } = await supabase
      .from('characters')
      .insert({
        campaign_id: store.campaignId,
        owner_user_id: store.characterRole === 'pc' ? user.id : null,
        character_role: store.characterRole,
        creation_method: 'manual',
        name: store.name,
        species_id: store.species_id,
        sex: store.sex,
        culture_id: store.culture_id,
        social_class: store.social_class,
        birth_year: store.birth_year,
        birth_month: store.birth_month,
        birth_day: store.birth_day,
        sunsign_id: store.sunsign_id,
        on_cusp: store.on_cusp,
        height_inches: store.height_inches,
        frame: store.frame,
        weight_lbs: store.weight_lbs,
        comeliness: store.comeliness,
        complexion: store.complexion,
        hair_color: store.hair_color,
        eye_color: store.eye_color,
        attr_str: store.attr_str, attr_sta: store.attr_sta,
        attr_dex: store.attr_dex, attr_agl: store.attr_agl,
        attr_eye: store.attr_eye, attr_hrg: store.attr_hrg,
        attr_sml: store.attr_sml, attr_voi: store.attr_voi,
        attr_int: store.attr_int, attr_aur: store.attr_aur,
        attr_wil: store.attr_wil,
        morality: store.morality,
        occupation_name: store.occupation_name || null,
        deity: store.deity || null,
        piety: store.piety,
        sibling_rank: store.sibling_rank,
        family_size: store.family_size,
        parent_status: store.parent_status,
        estrangement: store.estrangement,
      })
      .select()
      .single()

    if (error) {
      alert(`Error creating character: ${error.message}`)
      return
    }

    // Create snapshot
    await supabase.from('character_snapshots').insert({
      character_id: data.id,
      content_hash: 'initial',
      character_name: store.name,
      campaign_id: store.campaignId,
      character_role: store.characterRole,
      occupation_name: store.occupation_name || null,
      social_class: store.social_class,
      current_age: 720 - store.birth_year, // approximate
      funds_denarii: 0,
    })

    store.reset()
    navigate(`/character/${data.id}`)
  }

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Create Character</h2>
        <button onClick={() => { store.reset(); navigate(-1) }}>Cancel</button>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {STEPS.map((s, i) => (
          <button
            key={i}
            onClick={() => store.goToStep(i + 1)}
            style={{
              padding: '0.3rem 0.6rem',
              fontSize: '0.8rem',
              fontWeight: store.step === i + 1 ? 'bold' : 'normal',
              backgroundColor: store.step === i + 1 ? '#333' : '#eee',
              color: store.step === i + 1 ? '#fff' : '#333',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
            }}
          >
            {i + 1}. {s.label}
          </button>
        ))}
      </div>

      <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem' }}>
        {CurrentStep && <CurrentStep />}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={() => store.prevStep()} disabled={isFirst}
          style={{ padding: '0.5rem 1.5rem' }}>
          &larr; Back
        </button>
        {isLast ? (
          <button onClick={handleCreate}
            style={{ padding: '0.5rem 1.5rem', fontWeight: 'bold', backgroundColor: '#4a9', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Create Character
          </button>
        ) : (
          <button onClick={() => store.nextStep()}
            style={{ padding: '0.5rem 1.5rem' }}>
            Next &rarr;
          </button>
        )}
      </div>
    </div>
  )
}
